import { formatWizardPreselection } from '~/assets/util/formatters'
import Cookies from '~/assets/util/helpers/cookies'

export const state = () => {
	return {
		data: [],
		coupons: [],
		currency: 'usd',
		wizardSelection: null
	}
}

export const getters = {
	accessories (state) {
		return state.data
			.filter(item => item.type === 'accessory' && Array.isArray(item.prices) && item.prices.length)
			.map((item) => {
				const priceObj = (item.prices && item.prices[0]) || {}
				const metadata = { ...item.metadata, ...priceObj.metadata }
				let count = {
					...(item.metadata.accessoryType === 'outdoorTripod' && { count: 1 }),
					...(item.metadata.accessoryType === 'magicArm' && { count: 2 }),
					...(item.metadata.accessoryType === 'indoorTripod' && { count: 3 }),
					...(item.metadata.accessoryType === 'highOutdoorTripod' && { count: 4 }),
					...(item.metadata.accessoryType === 'outdoorTripodCarryBag' && { count: 5 }),
					...(item.metadata.accessoryType === 'highOutdoorCarryingCase' && { count: 6 })
				}

				if (!Object.keys(count).length) {
					count = { count: 100 } // for placing it to the end of list
				}

				return { ...item, ...priceObj, ...{ metadata }, ...count }
			})
			.sort((a, b) => a.count - b.count)
	},

	airAccessories (state, getters) {
		return getters.accessories.filter(item => item.metadata.cameraType === 'air')
	},

	actionAccessories (state, getters) {
		return getters.accessories.filter(item => item.metadata.cameraType === 'action')
	},

	cameras (state, getters) {
		return state.data
			.filter(item => item.type === 'camera')
			.reduce((acc, { prices, productId, metadata }) => {
				const accessories = getters.accessories
					.filter(item => item.metadata.cameraType === metadata.cameraType)
					.map((item) => {
						item.quantity = 1
						return item
					})

				prices.forEach((item) => {
					acc.push({
						...item,
						productId,
						accessories,
						metadata: {
							...metadata,
							...item.metadata,
							titles: item.metadata.titles || []
						}
					})
				})

				return acc
			}, [])
	},
	actionCamera (state, getters) {
		return getters.cameras.find(camera => camera.price && camera.metadata.cameraType === 'action')
	},
	airCamera (state, getters) {
		return getters.cameras.find(camera => camera.price && camera.metadata.cameraType === 'air')
	},
	airOwnCamera (state, getters) {
		return getters.cameras.find(camera => camera.price === 0 && camera.metadata.cameraType === 'air')
	},
	goProCamera (state, getters) {
		return getters.cameras.find(camera => camera.price === 0 && camera.metadata.cameraType === 'action' && camera.metadata?.priceType === 'camera')
	},

	subscriptions (state) {
		return state.data
			.filter(item => item.type === 'subscription' && !item.metadata?.bundle && !item.metadata?.plan)
			.reduce((acc, { prices, productId, metadata }) => {
				prices.forEach((item) => {
					acc.push({
						...item,
						productId,
						metadata: {
							...item.metadata,
							titles: item.metadata.titles || [],
							features: metadata.features || [],
							cameraType: metadata.cameraType
						}
					})
				})

				return acc
			}, [])
			.sort((a, b) => a.price - b.price)
	},
	teamSubscriptions (state, getters) {
		return getters.subscriptions.filter(sub => sub.metadata.type === 'team')
	},
	clubSubscriptions (state, getters) {
		return getters.subscriptions.filter(sub => sub.metadata.type === 'club')
	},
	installments (state, getters, rootGetters) {
		// TODO: get installmet percents from coupons
		const percentFeePeriodMap = {
			12: 0.04,
			24: 0.08,
			36: 0.12
		}

		const findCoupon = (period, accountType) => {
			const origin = rootGetters.user.location?.code || 'xx'

			let percentUS = 0
			let usePercent = true
			if (state.coupons) {
				const couponItem = state.coupons.find((coupon) => {
					return coupon.criteries.service &&
						coupon.criteries.service.period === period &&
						coupon.criteries.service.accountType === accountType &&
						coupon.regions.includes(origin)
				})

				if (couponItem) {
					percentUS = couponItem?.details?.discount / 100 || 0
					if (couponItem.details.type && couponItem.details.type === 'included') {
						usePercent = false
					}
				}
			}
			return { percentUS, usePercent }
		}

		return state.data
			.filter(item => item.type === 'service')
			.reduce((acc, { prices, productId, metadata }) => {
				prices.forEach((item) => {
					const { percentUS, usePercent } = findCoupon(item.period, item.metadata.type)

					acc.push({
						...item,
						productId,
						percent: percentFeePeriodMap[item.period],
						percentUS,
						usePercent,
						intervalCount: item.period,
						metadata: {
							...item.metadata,
							cameraType: metadata.cameraType
						}
					})
				})

				return acc
			}, [])
			.sort((a, b) => a.price - b.price)
	},
	teamInstallments (state, getters) {
		return getters.installments.filter(inst => inst.metadata.type === 'team')
	},
	clubInstallments (state, getters) {
		return getters.installments.filter(inst => inst.metadata.type === 'club')
	},
	coupons (state, getters) {
		return state.coupons
	},
	bundles (state) {
		return state.data
			.filter(item => item.type === 'bundle')
	},
	bundleSubscriptions (state) {
		return state.data
			.filter(item => item.type === 'subscription' && item.metadata?.bundle)
			.reduce((acc, { prices, productId, metadata }) => {
				prices.forEach((item) => {
					acc.push({
						...item,
						productId,
						metadata: {
							...item.metadata,
							titles: item.metadata.titles || [],
							features: metadata.features || [],
							cameraType: metadata.cameraType,
							bundleType: metadata.bundle
						}
					})
				})

				return acc
			}, [])
			.sort((a, b) => a.price - b.price)
	},
	litePlanSubscriptions (state) {
		return state.data
			.filter(item => item.type === 'subscription' && item.metadata?.plan === 'lite')
			.reduce((acc, { prices, productId, metadata }) => {
				prices.forEach((item) => {
					acc.push({
						...item,
						productId,
						metadata: {
							...item.metadata,
							titles: item.metadata.titles || [],
							features: metadata.features || [],
							cameraType: metadata.cameraType,
							plan: metadata.plan
						}
					})
				})

				return acc
			}, [])
			.sort((a, b) => a.price - b.price)
	},
	currency (state) {
		return state.currency
	}
}

export const actions = {
	async fetchProducts ({ commit, rootState, rootGetters }, ignoreSport = false) {
		try {
			const sportType = rootState.teams.selected && rootState.teams.selected.attributes.sportType
			const origin = rootGetters['user/origin']
			const isClub = rootGetters['user/accountType'] === 'club'
			const queryParams = {
				origin,
				include: 'promotions',
				sportType: isClub || ignoreSport ? undefined : sportType
			}
			const { data, included } = await this.$axios.$get('/api/v1/payment/products/profile/1', { params: queryParams, skipAuth: true })

			this.$log.debug('[ProductsStore] Got `products` from API', data)

			commit('setProducts', data)

			const product = data.find(item => Array.isArray(item.prices) && item.prices.length)

			if (product?.prices[0]) {
				commit('setCurrency', product.prices[0].currency)
			}
			commit('setCoupons', included)
		} catch (error) {
			commit('setProducts', [])
			commit('setCoupons', [])
			this.$log.error('[ProductsStore] Error during `fetchProducts`', error)
		}
	},

	async submitPaymentCheckout ({ rootGetters }, data) {
		const origin = rootGetters['user/origin']
		const stripe = window.Stripe(process.env.stripeApiKey)

		const newPaymentSession = (payload) => {
			const body = { ...payload, origin }

			// Share A Sale integration with Payment BE
			// Check comments: https://pixellot.atlassian.net/browse/NPS-14911
			const sscid = Cookies.get('sas_sscid')

			if (sscid) {
				body.affiliate = { sscid }
			}

			return this.$axios.$post('/api/v1/payment/payments/create', body)
		}
		const { sessionId } = await newPaymentSession(data)

		await stripe.redirectToCheckout({ sessionId })
	}
}

export const mutations = {
	setProducts (state, payload) {
		state.data = payload
	},
	setCoupons (state, payload) {
		state.coupons = payload
	},
	setCurrency (state, currency) {
		state.currency = currency

		this.$log.info(`Set 'currency' as ${currency}`)
	},
	setWizardSelection (state, payload) {
		let selection = null

		try {
			selection = payload && formatWizardPreselection(payload)
		} catch (e) {
			this.$log.error('Failed during formatting selection for wizard', payload)
		}

		state.wizardSelection = selection

		this.$log.info('Set wizard selection data', payload)
	},
	resetWizardSelection (state) {
		state.wizardSelection = null
	}
}
